import React, { useMemo, useState } from 'react';
import { Building2, FileSpreadsheet, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const numberValue = (...values) => {
  const value = values.find(item => item !== undefined && item !== null && item !== '');
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const organizationValue = (row, keys) => {
  const key = keys.find(item => row?.[item] !== undefined && row?.[item] !== null);
  return key ? row[key] : '';
};

export const OrgSummaryTable = ({ data = [], isLoading = false }) => {
  const navigate = useNavigate();
  const [type, setType] = useState('Corporation');
  const [search, setSearch] = useState('');

  const organizations = useMemo(() => {
    const grouped = new Map();
    (Array.isArray(data) ? data : []).forEach(row => {
      const code = String(organizationValue(row, ['org_code', 'org_id', 'OrgCode', 'org', 'organization_code']) || '').trim();
      const name = String(organizationValue(row, ['org_name', 'OrgName', 'organization_name']) || code || 'Unknown Organization').trim();
      const apiType = String(organizationValue(row, ['type', 'org_type', 'OrgType']) || '').trim();
      const key = (code || name).toLowerCase();
      const current = grouped.get(key) || {
        code, name, apiType, assets: 0, completed: 0, today: 0, yesterday: 0,
        newAssessments: 0, areaViolations: 0, usageViolations: 0,
        newProfessionalTax: 0, newTradeLicense: 0
      };
      current.assets += numberValue(row.total, row.total_assets, row.asset_count, row.assets);
      current.completed += numberValue(row.completed, row.completed_count, row.total_completed);
      current.today += numberValue(row.today, row.today_completed);
      current.yesterday += numberValue(row.yesterday, row.yesterday_completed);
      current.newAssessments += numberValue(row.newass, row.new_assessments);
      current.areaViolations += numberValue(row.aviolat, row.area_violations);
      current.usageViolations += numberValue(row.NonRes, row.non_res, row.usage_violations);
      current.newProfessionalTax += numberValue(row.newptax, row.new_professional_tax);
      current.newTradeLicense += numberValue(row.newtlic, row.new_trade_license);
      grouped.set(key, current);
    });
    return [...grouped.values()].map(item => ({
      ...item,
      pending: Math.max(0, item.assets - item.completed),
      type: /corporation/i.test(item.apiType)
        ? 'Corporation'
        : /municipality/i.test(item.apiType)
          ? 'Municipality'
          : /corporation/i.test(item.name) ? 'Corporation' : 'Municipality'
    }));
  }, [data]);

  const visible = organizations.filter(item => item.type === type &&
    `${item.name} ${item.code}`.toLowerCase().includes(search.trim().toLowerCase()));

  const openCompleted = (organization, period) => {
    navigate(`/organization-assets/${encodeURIComponent(organization.code || organization.name)}`, {
      state: { orgCode: organization.code, orgName: organization.name, period, status: 'completed' }
    });
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const orderedOrganizations = [
      ...organizations.filter(item => item.type === 'Corporation'),
      ...organizations.filter(item => item.type === 'Municipality')
    ];
    const rows = orderedOrganizations.map((item, index) => ({
          'S.No': index + 1,
          'Type': item.type,
          'Code': item.code,
          'Name': item.name,
          'Assets to be Verified': item.assets,
          'Verification Completed': item.completed,
          'Balance to be Verified': item.pending,
          'No. of New Assessment Identified': item.newAssessments,
          'No. of Area Violation': item.areaViolations,
          'No. of Usage Violation': item.usageViolations,
          'No. of New Prof. Tax': item.newProfessionalTax,
          'No. of New Trade License': item.newTradeLicense
        }));
    const worksheet = XLSX.utils.json_to_sheet(rows, {
        header: [
          'S.No', 'Type', 'Code', 'Name', 'Assets to be Verified', 'Verification Completed',
          'Balance to be Verified', 'No. of New Assessment Identified', 'No. of Area Violation',
          'No. of Usage Violation', 'No. of New Prof. Tax', 'No. of New Trade License'
        ]
      });
    worksheet['!cols'] = [
      { wch: 7 }, { wch: 16 }, { wch: 12 }, { wch: 34 }, { wch: 22 },
      { wch: 24 }, { wch: 24 }, { wch: 34 }, { wch: 24 }, { wch: 25 },
      { wch: 24 }, { wch: 26 }
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Organization Performance');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `CMA_Organization_Performance_${date}.xlsx`);
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Building2 className="h-5 w-5 text-blue-600" />Organization Performance</h2>
          <p className="mt-1 text-sm text-gray-500">Assets and completed survey status by organization</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex rounded-lg bg-gray-100 p-1">
            {['Corporation', 'Municipality'].map(item => <button key={item} onClick={() => setType(item)} className={`rounded-md px-4 py-2 text-sm font-medium ${type === item ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>{item}</button>)}
          </div>
          <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search organization" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm sm:w-64" /></div>
          <button type="button" onClick={exportToExcel} disabled={isLoading || organizations.length === 0} className="inline-flex items-center justify-center rounded-lg border border-green-600 px-3 py-2 text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50" title="Download Excel" aria-label="Download organization performance Excel"><FileSpreadsheet className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] table-fixed divide-y divide-gray-200 text-sm">
          <colgroup>
            <col className="w-[42px]" />
            <col className="w-[58px]" />
            <col className="w-[145px]" />
            <col className="w-[82px]" />
            <col className="w-[58px]" />
            <col className="w-[68px]" />
            <col className="w-[48px]" />
            <col className="w-[86px]" />
            <col className="w-[92px]" />
            <col className="w-[72px]" />
            <col className="w-[74px]" />
            <col className="w-[76px]" />
            <col className="w-[78px]" />
          </colgroup>
          <thead className="bg-gray-50 text-center text-[10px] uppercase leading-tight tracking-normal text-gray-500">
            <tr className="border-b border-gray-200">
              <th rowSpan="2" className="px-1 py-2 text-center align-middle">S.No</th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle">Code</th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle">Name</th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle"><span className="block">Assets</span><span className="block">to be</span><span className="block">Verified</span></th>
              <th colSpan="3" className="px-1 py-2 text-center">Verification Completed</th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle"><span className="block">Balance</span><span className="block">to be</span><span className="block">Verified</span></th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle"><span className="block">New</span><span className="block">Assessment</span><span className="block">Identified</span></th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle"><span className="block">Area</span><span className="block">Violation</span></th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle"><span className="block">Usage</span><span className="block">Violation</span></th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle"><span className="block">New Prof.</span><span className="block">Tax</span></th>
              <th rowSpan="2" className="px-1 py-2 text-center align-middle"><span className="block">New Trade</span><span className="block">License</span></th>
            </tr>
            <tr><th className="px-1 py-2 text-center">Today</th><th className="px-1 py-2 text-center">Yesterday</th><th className="px-1 py-2 text-center">All</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? <tr><td colSpan="13" className="px-5 py-12 text-center text-gray-500">Loading organization data...</td></tr> : visible.length === 0 ? <tr><td colSpan="13" className="px-5 py-12 text-center text-gray-500">No {type.toLowerCase()} data found.</td></tr> : visible.map((item, index) => (
              <tr key={item.code || item.name} className="hover:bg-gray-50">
                <td className="px-1 py-2 text-center text-gray-600">{index + 1}</td>
                <td className="px-1 py-2 font-medium text-gray-700">{item.code || '-'}</td>
                <td className="px-1 py-2 font-semibold text-gray-900">{item.name}</td>
                <td className="px-1 py-2 text-center font-semibold">{item.assets.toLocaleString('en-IN')}</td>
                <td className="px-1 py-2 text-center"><button onClick={() => openCompleted(item, 'today')} className="font-semibold text-blue-600 hover:underline">{item.today.toLocaleString('en-IN')}</button></td>
                <td className="px-1 py-2 text-center"><button onClick={() => openCompleted(item, 'yesterday')} className="font-semibold text-blue-600 hover:underline">{item.yesterday.toLocaleString('en-IN')}</button></td>
                <td className="px-1 py-2 text-center"><button onClick={() => openCompleted(item, 'all')} className="font-semibold text-green-600 hover:underline">{item.completed.toLocaleString('en-IN')}</button></td>
                <td className="px-1 py-2 text-center font-semibold text-amber-600">{item.pending.toLocaleString('en-IN')}</td>
                <td className="px-1 py-2 text-center font-semibold text-red-600">{item.newAssessments.toLocaleString('en-IN')}</td>
                <td className="px-1 py-2 text-center font-semibold text-red-600">{item.areaViolations.toLocaleString('en-IN')}</td>
                <td className="px-1 py-2 text-center font-semibold text-red-600">{item.usageViolations.toLocaleString('en-IN')}</td>
                <td className="px-1 py-2 text-center font-semibold text-red-600">{item.newProfessionalTax.toLocaleString('en-IN')}</td>
                <td className="px-1 py-2 text-center font-semibold text-red-600">{item.newTradeLicense.toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
